// src/components/onboarding/onboardingReview.js
//
// Pure helpers behind the two onboarding review modals (Stage A contract,
// Stages B & C tickets). No React, no imports — testable without MSAL.
//
// ⛔ THE RULE THESE SERVE: /api/onboarding/run REBUILDS the plan server-side
// and accepts only a narrow, named allowlist of overrides. These helpers turn
// what Craig did in a modal into exactly that allowlist — never an edited plan
// object. Anything the modal shows that is not produced here is display-only.

const isNum = (v) => Number.isFinite(Number(v));

/* ── Stage A ────────────────────────────────────────────────────────────── */

/** The included lines with any quantity edits applied — for the DISPLAYED total only;
 *  the server recomputes. */
export function reviewedServiceLines(lines, excluded, quantities) {
  return (lines || [])
    .filter((l) => !excluded.has(String(l.serviceID)))
    .map((l) => {
      const q = quantities[String(l.serviceID)];
      const n = q === "" || q === undefined || q === null ? NaN : Number(q);
      return Number.isFinite(n) && n > 0 ? { ...l, unitChange: n } : l;
    });
}

export function reviewedMonthlyTotal(lines, excluded, quantities) {
  return reviewedServiceLines(lines, excluded, quantities).reduce((t, l) => t + l.unitPrice * l.unitChange, 0);
}

/** The Stage A allowlist, and nothing else. A name equal to the built one or
 *  blank is omitted (the server keeps the built value); a quantity equal to
 *  the plan's or invalid is omitted. */
export function stageAOverrides({ lines, builtName, contractName, excluded, quantities }) {
  const out = {};
  const name = String(contractName || "").trim();
  if (name && name !== builtName) out.contractName = name;
  const ex = (lines || []).filter((l) => excluded.has(String(l.serviceID))).map((l) => String(l.serviceID));
  if (ex.length) out.excludeServiceLines = ex;
  const qty = {};
  (lines || []).forEach((l) => {
    if (excluded.has(String(l.serviceID))) return;
    const q = quantities[String(l.serviceID)];
    if (q === "" || q === undefined || q === null) return;
    const n = Number(q);
    if (Number.isFinite(n) && n > 0 && n !== l.unitChange) qty[String(l.serviceID)] = n;
  });
  if (Object.keys(qty).length) out.serviceLineQuantities = qty;
  return out;
}

/* ── Stages B & C ───────────────────────────────────────────────────────── */

export const STAGE_C_KEY = "stageC";

/** The record entry for a Stage B ticket, recognised the three ways runStageB
 *  recognises it — matchKey (the identity), the rendered title, the legacy
 *  title. Mirrors the service; a ticket this returns for is ALREADY CREATED. */
export function ticketEntryFor(record, t) {
  const ids = (record && record.ticketIds) || [];
  return ids.find((x) =>
    (t.matchKey && x.matchKey === t.matchKey) || x.blockTitle === t.title || x.blockTitle === t.legacyTitle) || null;
}

/** Every ticket the modal reviews: Stage B's in plan order, then Stage C's.
 *  `existing` is non-null for one already created — shown as done, not editable. */
export function ticketsForReview(plan, record) {
  const items = (plan?.stageB?.tickets || []).map((t) => ({
    key: t.matchKey, stage: "B", ticket: t, existing: ticketEntryFor(record, t),
  }));
  const c = plan?.stageC?.ticket;
  if (c) {
    items.push({
      key: STAGE_C_KEY, stage: "C",
      ticket: { ...c, description: c.description || `Onboarding for ${plan.clientName} (${plan.tier}).` },
      existing: record && record.onboardingTicketId
        ? { ticketId: record.onboardingTicketId, ticketNumber: record.onboardingTicketNumber || null }
        : null,
    });
  }
  return items;
}

/** The editable state the modal starts from: every not-yet-created ticket
 *  included, seeded with the plan's title and description, unclassified. */
export function initialTicketEdits(items) {
  const out = {};
  items.forEach((it) => {
    if (it.existing) return;
    out[it.key] = { include: true, title: it.ticket.title || "", description: it.ticket.description || "", issueType: "", subIssueType: "" };
  });
  return out;
}

/** The Stage B/C allowlist from the edits. Only not-yet-created tickets; a
 *  title or description equal to the seed is omitted (the server keeps the
 *  built text); `skip: true` for an unticked ticket; classification as
 *  number-or-null only when something was chosen. ⚠ matchKey is the KEY of the
 *  stageB map and never a value — the identity cannot be edited from here. */
export function buildTicketOverrides(items, edits) {
  const stageB = {};
  let stageC;
  items.forEach((it) => {
    if (it.existing) return;
    const e = edits[it.key];
    if (!e) return;
    const o = {};
    if (!e.include) o.skip = true;
    const title = String(e.title || "").trim();
    if (title && title !== it.ticket.title) o.title = title;
    const description = String(e.description || "");
    if (description.trim() && description !== it.ticket.description) o.description = description;
    if ((e.issueType !== "" && e.issueType != null) || (e.subIssueType !== "" && e.subIssueType != null)) {
      o.issueType = isNum(e.issueType) && e.issueType !== "" ? Number(e.issueType) : null;
      o.subIssueType = isNum(e.subIssueType) && e.subIssueType !== "" ? Number(e.subIssueType) : null;
    }
    if (!Object.keys(o).length) return;
    if (it.stage === "C") stageC = o; else stageB[it.key] = o;
  });
  const out = {};
  if (Object.keys(stageB).length) out.stageB = stageB;
  if (stageC) out.stageC = stageC;
  return out;
}

/** What happened to one reviewed ticket after a run, from the record the run
 *  returned. Stage C's checklist attach is reported as an OUTCOME — it cannot
 *  be read back (TicketChecklistLibraries is canQuery=false). */
export function ticketOutcome(item, record, edits) {
  const e = edits && edits[item.key];
  if (item.stage === "C") {
    if (!record?.onboardingTicketId) {
      if (e && !e.include) return { state: "skipped" };
      const err = (record?.errors || []).find((x) => x.stage === "C" && /Onboarding ticket/.test(x.message));
      return { state: "failed", error: err ? err.message : null };
    }
    const attach = record.checklistAttached === true
      ? { state: "reported-ok", status: record.checklistAttachResult?.status ?? null }
      : { state: "failed", error: ((record.errors || []).find((x) => x.stage === "C" && /[Cc]hecklist/.test(x.message)) || {}).message || null };
    return { state: "created", ticketNumber: record.onboardingTicketNumber || null, ticketId: record.onboardingTicketId, attach };
  }
  const entry = ticketEntryFor(record, item.ticket);
  if (entry) return { state: "created", ticketNumber: entry.ticketNumber || null, ticketId: entry.ticketId, contractId: entry.contractId || null };
  if (e && !e.include) return { state: "skipped" };
  const err = (record?.errors || []).find((x) => x.stage === "B" && x.message.includes(`"${(e && e.title) || item.ticket.title}"`));
  return { state: "failed", error: err ? err.message : null };
}
